import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import {
    SilProjectDevice,
    SilProjectDeviceNested,
} from "server/models"
import { StateType } from "client/utils"
import {
    StoreType,
    SilDeviceType,
    SilDeviceNestedType,

    LoadSilDeviceById,
    LoadAccountPackageUsage,
    SaveSilDevice,
    SilProjectDeviceUpdateByFilters,
    SilProjectDeviceUpdateRequired,
    CountCalculatedSafetyFunctions,
    UpdateSilResultProjectDevice,
    IncreaseSilCalculations,
    DeleteSilNestedDevices,
    AddSilNestedDevice,

    closeModal,
    setSilDeviceReloadList,
    setSilDeviceReloadView,
    setSystemPackageUsage,
    setSilUpdateDeviceTargetSubsystem,
} from "client/store"
import {
    PageForm,
    Label,
    Input,
    Textarea,
    Feedback,
    FileLoader,
    Collapse,
    Button,
    SpinnerIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
} from "client/components"

import Model from "./model"
import CalculatedData from "./calculatedData"

import {
    RowStyled,
    FormGroupStyled,
    ButtonWrapper,
} from "../styled"

let isMounted: boolean

let updated = false
let recalculationRequired = false
let originEqual = false
let saveMode = 0

interface UpdateSubsystemProps {
    disableUpdateModel: boolean
}

const Form = ({
    disableUpdateModel,
}: UpdateSubsystemProps) => {
    const target = useSelector((state: StoreType) => state.silUpdate.subsystem.targetDevice)
    const origin = useSelector((state: StoreType) => state.silUpdate.subsystem.originDevice)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageUsage = useSelector((state: StoreType) => state.system.packageUsage)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [updateAllState, setUpdateAllState] = useState<StateType>(IDLE)
    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [recalculateCount, setRecalculateCount] = useState(0)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const setTargetDevice = (device: SilDeviceType) => {
        dispatch(setSilUpdateDeviceTargetSubsystem(device))
    }

    const updateAll = () => {
        const deviceUpdated = {
            ...target,

            name: origin.name,
            manufacturer: origin.manufacturer,
            logoId: origin.logoId,
            logo: origin.logo,
            description: origin.description,
            reference: origin.reference,
            status: origin.status,

            lowDemandMode: origin.lowDemandMode,
            highDemandMode: origin.highDemandMode,
            continuousMode: origin.continuousMode,
            type: origin.type,
            logicSolver: origin.logicSolver,
            provenInUse: origin.provenInUse,
            failureRate: origin.failureRate,

            sd: origin.sd,
            su: origin.su,
            dd: origin.dd,
            du: origin.du,

            nestedDevices: disableUpdateModel ? target.nestedDevices : origin.nestedDevices,
        } as SilDeviceType

        setTargetDevice(deviceUpdated)
        onSave(deviceUpdated)
    }

    const onClose = () => {
        if (!isMounted) return

        if (isMounted && modals.length) {
            dispatch(closeModal())
        }
    }

    const onCancelConfirm = () => {
        setSaveState(IDLE)
        setUpdateAllState(IDLE)
        setRecalculateCount(0)
    }

    const onSave = (targetDevice: SilDeviceType) => {
        if (saveMode === 1) setUpdateAllState(PENDING)
        if (saveMode === 2) setSaveState(PENDING)

        LoadSilDeviceById(targetDevice.id, userData.accountId, project.id).then((existingDevice) => {
            updated = false
            recalculationRequired = false

            if (
                existingDevice && ( // saving on account/project level
                    existingDevice.manufacturer !== targetDevice.manufacturer
                    || existingDevice.name !== targetDevice.name
                    || existingDevice.description !== targetDevice.description
                    || existingDevice.reference !== targetDevice.reference
                    || existingDevice.logoId !== targetDevice.logoId
                    || existingDevice.logicSolver !== targetDevice.logicSolver
                    || existingDevice.failureRate !== targetDevice.failureRate
                    || existingDevice.provenInUse !== targetDevice.provenInUse
                    || existingDevice.type !== targetDevice.type
                    || existingDevice.lowDemandMode !== targetDevice.lowDemandMode
                    || existingDevice.highDemandMode !== targetDevice.highDemandMode
                    || existingDevice.continuousMode !== targetDevice.continuousMode
                    || existingDevice.sd !== targetDevice.sd
                    || existingDevice.su !== targetDevice.su
                    || existingDevice.dd !== targetDevice.dd
                    || existingDevice.du !== targetDevice.du
                    || existingDevice.status !== targetDevice.status
                )
            ) {
                updated = true
            }

            if (
                existingDevice && project.id && ( // saving on project level
                    existingDevice.type !== targetDevice.type
                    || existingDevice.lowDemandMode !== targetDevice.lowDemandMode
                    || existingDevice.highDemandMode !== targetDevice.highDemandMode
                    || existingDevice.continuousMode !== targetDevice.continuousMode
                    || existingDevice.logicSolver !== targetDevice.logicSolver
                    || existingDevice.provenInUse !== targetDevice.provenInUse
                    || existingDevice.failureRate !== targetDevice.failureRate
                    || existingDevice.sd !== targetDevice.sd
                    || existingDevice.su !== targetDevice.su
                    || existingDevice.dd !== targetDevice.dd
                    || existingDevice.du !== targetDevice.du
                )
            ) {
                recalculationRequired = true
            }

            if (
                origin.manufacturer === targetDevice.manufacturer
                && origin.name === targetDevice.name
                && origin.logoId === targetDevice.logoId
                && origin.description === targetDevice.description
                && origin.reference === targetDevice.reference
                && origin.logicSolver === targetDevice.logicSolver
                && origin.failureRate === targetDevice.failureRate
                && origin.provenInUse === targetDevice.provenInUse
                && origin.type === targetDevice.type
                && origin.lowDemandMode === targetDevice.lowDemandMode
                && origin.highDemandMode === targetDevice.highDemandMode
                && origin.continuousMode === targetDevice.continuousMode
                && origin.sd === targetDevice.sd
                && origin.su === targetDevice.su
                && origin.dd === targetDevice.dd
                && origin.du === targetDevice.du
                && origin.status === targetDevice.status
            ) {
                originEqual = true
            }

            if (recalculationRequired) {
                const projectTargetDevice = targetDevice as SilProjectDevice

                // check, if there are functions that use this subsystem
                CountCalculatedSafetyFunctions({
                    projectId: project.id,
                    deviceId: projectTargetDevice.deviceId || null,
                    projectDeviceId: !projectTargetDevice.deviceId ? projectTargetDevice.id : null,
                }).then((count) => {
                    if (count) {
                        LoadAccountPackageUsage(userData.accountId)
                        .then((res) => {
                            dispatch(setSystemPackageUsage(res.packageUsage))
                            // show confirm
                            setRecalculateCount(count)
                        })
                    } else {
                        save(targetDevice)
                    }
                })
            } else {
                save(targetDevice)
            }
        })
    }

    const save = (targetDevice: SilDeviceType) => {
        const newDeviceData = {
            ...targetDevice,
            updated: !originEqual, // clear "updated" mark for equal devices
        }

        SaveSilDevice(
            targetDevice.id,
            newDeviceData,
            null, // don't check max number when saving existing device
            lang,
        ).then((res) => {
            const promiseArray: Promise<any>[] = []
            const projectTargetDevice = targetDevice as SilProjectDevice

            // update depending devices
            if (!res.validation) {
                // saving account subsystem
                if (!project.id && updated) {
                    promiseArray.push(
                        // update project subsystems imported from account
                        SilProjectDeviceUpdateByFilters({ deviceId: targetDevice.id }),

                        // update project subsystems that based on this device
                        SilProjectDeviceUpdateRequired({ deviceId: targetDevice.id }),
                    )
                }

                // saving subsystem on project level
                if (project.id && updated) {
                    // update project subsystems that based on this device
                    if (projectTargetDevice.deviceId) { // subsystem imported from account
                        promiseArray.push(
                            SilProjectDeviceUpdateRequired({ deviceId: projectTargetDevice.deviceId }),
                        )
                    }
                    if (!projectTargetDevice.deviceId) { // subsystem created on project level
                        promiseArray.push(
                            SilProjectDeviceUpdateRequired({ projectDeviceId: projectTargetDevice.id }),
                        )
                    }
                }

                // saving project subsystem
                if (project.id) {
                    promiseArray.push(
                        UpdateSilResultProjectDevice({
                            projectId: project.id,
                            deviceId: projectTargetDevice.deviceId,
                            projectDeviceId: !projectTargetDevice.deviceId ? projectTargetDevice.id : null,
                        }),
                    )

                    if (recalculationRequired) {
                        promiseArray.push(
                            IncreaseSilCalculations(userData.accountId, dispatch),
                        )
                    }
                }
            }

            Promise.all(promiseArray).then(() => {
                // save nested devices
                const promiseSaveNested: Promise<any>[] = []
                promiseSaveNested.push(
                    DeleteSilNestedDevices(project.id, targetDevice.id)
                    .then(() => {
                        const promiseAddDevices: Promise<any>[] = []
                        targetDevice.nestedDevices.forEach((nested) => {
                            const newNestedData: SilDeviceNestedType = {
                                ...nested,
                                id: null,
                                parentDeviceId: targetDevice.id,
                            }
                            promiseAddDevices.push(
                                AddSilNestedDevice(project.id, newNestedData),
                            )
                        })

                        return Promise.all(promiseAddDevices)
                    }),
                )

                Promise.all(promiseSaveNested).then(() => {
                    if (isMounted) {
                        setUpdateAllState(IDLE)
                        setSaveState(IDLE)
                    }
                    if (res.validation) return

                    dispatch(setSilDeviceReloadList(false))
                    dispatch(setSilDeviceReloadView(false))
                    setTimeout(() => {
                        dispatch(setSilDeviceReloadList(true))
                        dispatch(setSilDeviceReloadView(true))
                    }, 0)

                    onClose()
                })
            })
        })
    }


    const { maxCalculations, usedCalculations } = packageUsage

    let leftCalculations = ""
    if (maxCalculations === null) leftCalculations = lang.unlimitedCalculations
    else leftCalculations = lang.leftCalculations.replace("{0}", usedCalculations.toString()).replace("{1}", maxCalculations.toString())

    // check if origin and target nested devices are equal
    let nestedDevicesNotEqual = false
    if (origin.nestedDevices && target.nestedDevices) {
        origin.nestedDevices.forEach((originNested, index) => {
            const targetNested = target.nestedDevices[index]

            if (
                originNested && targetNested
                && (
                    originNested.deviceId !== targetNested.deviceId
                    || originNested.qidProductId !== targetNested.qidProductId
                    || originNested.qidModelId !== targetNested.qidModelId
                    || originNested.qidSafetyFunctionId !== targetNested.qidSafetyFunctionId
                    || originNested.qidOptionId !== targetNested.qidOptionId
                    || originNested.qidDatapointId !== targetNested.qidDatapointId
                    || originNested.qidModelName !== targetNested.qidModelName
                    || originNested.qidSafetyFunctionName !== targetNested.qidSafetyFunctionName
                    || originNested.qidOptionName !== targetNested.qidOptionName
                    || originNested.qidDatapointDate !== targetNested.qidDatapointDate
                    || originNested.qidMethod !== targetNested.qidMethod
                    || originNested.manufacturer !== targetNested.manufacturer
                    || originNested.name !== targetNested.name
                    || originNested.logoId !== targetNested.logoId
                    || originNested.type !== targetNested.type
                    || originNested.mode !== targetNested.mode
                    || originNested.logicSolver !== targetNested.logicSolver
                    || originNested.provenInUse !== targetNested.provenInUse
                    || originNested.sd !== targetNested.sd
                    || originNested.su !== targetNested.su
                    || originNested.dd !== targetNested.dd
                    || originNested.du !== targetNested.du
                    || originNested.order !== targetNested.order
                    || (originNested as SilProjectDeviceNested).projectDeviceId !== (targetNested as SilProjectDeviceNested).projectDeviceId
                )
            ) {
                nestedDevicesNotEqual = true
            }
        })
    }

    return (
        <div>
            <RowStyled top titleRow>
                <FormGroupStyled><Label>{lang.originSubsystem}</Label></FormGroupStyled>
                <FormGroupStyled><Label>{lang.currentSubsystem}</Label></FormGroupStyled>
            </RowStyled>
            <RowStyled danger={origin.name !== target.name}>
                <FormGroupStyled>
                    <Label>{lang.name}</Label>
                    <Input type="text" value={origin.name} readOnly />
                </FormGroupStyled>
                <FormGroupStyled>
                    <Label>{lang.name}</Label>
                    <Input type="text" value={target.name} readOnly />
                </FormGroupStyled>
                {
                    origin.name !== target.name && (
                        <ButtonWrapper>
                            <Button onClick={() => setTargetDevice({ ...target, name: origin.name })}>{lang.update}</Button>
                        </ButtonWrapper>
                    )
                }
            </RowStyled>
            <RowStyled danger={origin.manufacturer !== target.manufacturer}>
                <FormGroupStyled>
                    <Label>{lang.manufacturer}</Label>
                    <Input type="text" value={origin.manufacturer} readOnly />
                </FormGroupStyled>
                <FormGroupStyled>
                    <Label>{lang.manufacturer}</Label>
                    <Input type="text" value={target.manufacturer} readOnly />
                </FormGroupStyled>
                {
                    origin.manufacturer !== target.manufacturer && (
                        <ButtonWrapper>
                            <Button onClick={() => setTargetDevice({ ...target, manufacturer: origin.manufacturer })}>{lang.update}</Button>
                        </ButtonWrapper>
                    )
                }
            </RowStyled>
            <RowStyled danger={origin.logoId !== target.logoId}>
                <FormGroupStyled>
                    <Label>{lang.image}</Label>
                    <FileLoader value={origin.logo} readOnly />
                </FormGroupStyled>
                <FormGroupStyled>
                    <Label>{lang.image}</Label>
                    <FileLoader value={target.logo} readOnly />
                </FormGroupStyled>
                {
                    origin.logoId !== target.logoId && (
                        <ButtonWrapper>
                            <Button onClick={() => setTargetDevice({
                                ...target,
                                logoId: origin.logoId,
                                logo: origin.logo,
                            })}
                            >
                                {lang.update}
                            </Button>
                        </ButtonWrapper>
                    )
                }
            </RowStyled>
            <RowStyled danger={origin.description !== target.description}>
                <FormGroupStyled>
                    <Label>{lang.description}</Label>
                    <Textarea value={origin.description} readOnly />
                </FormGroupStyled>
                <FormGroupStyled>
                    <Label>{lang.description}</Label>
                    <Textarea value={target.description} readOnly />
                </FormGroupStyled>
                {
                    origin.description !== target.description && (
                        <ButtonWrapper>
                            <Button onClick={() => setTargetDevice({ ...target, description: origin.description })}>{lang.update}</Button>
                        </ButtonWrapper>
                    )
                }
            </RowStyled>
            <RowStyled danger={origin.reference !== target.reference}>
                <FormGroupStyled>
                    <Label>{lang.reference}</Label>
                    <Input type="text" value={origin.reference} readOnly />
                </FormGroupStyled>
                <FormGroupStyled>
                    <Label>{lang.reference}</Label>
                    <Input type="text" value={target.reference} readOnly />
                </FormGroupStyled>
                {
                    origin.reference !== target.reference && (
                        <ButtonWrapper>
                            <Button onClick={() => setTargetDevice({ ...target, reference: origin.reference })}>{lang.update}</Button>
                        </ButtonWrapper>
                    )
                }
            </RowStyled>
            <RowStyled danger={origin.status !== target.status} bottom>
                <FormGroupStyled>
                    <Label>{lang.status}</Label>
                    <Input type="text" value={lang[origin.status]} readOnly />
                </FormGroupStyled>
                <FormGroupStyled>
                    <Label>{lang.status}</Label>
                    <Input type="text" value={lang[target.status]} readOnly />
                </FormGroupStyled>
                {
                    origin.status !== target.status && (
                        <ButtonWrapper>
                            <Button onClick={() => setTargetDevice({ ...target, status: origin.status })}>{lang.update}</Button>
                        </ButtonWrapper>
                    )
                }
            </RowStyled>

            <Collapse show title={lang.updatedSubsystem}>
                <Model nestedDevices={origin.nestedDevices} />
            </Collapse>

            <Collapse show title={lang.existingSubsystem}>
                <Model nestedDevices={target.nestedDevices} />
            </Collapse>

            <Collapse show title={lang.subsystemData}>
                <CalculatedData
                    existingSubsystem={target}
                    updatedSubsystem={origin}
                    nestedDevices={origin.nestedDevices}
                />

                {
                    /*
                    if only the "name" or "manufacturer" of the nested device is updated
                    then it will not affect on calculation results, but we still need to update the model
                    */
                    nestedDevicesNotEqual && !disableUpdateModel && (
                        <RowStyled danger>
                            <PageForm>
                                <Button onClick={() => setTargetDevice({
                                    ...target,
                                    nestedDevices: origin.nestedDevices,

                                    // update calculated subsystem details
                                    type: origin.type,
                                    lowDemandMode: origin.lowDemandMode,
                                    highDemandMode: origin.highDemandMode,
                                    continuousMode: origin.continuousMode,
                                    logicSolver: origin.logicSolver,
                                    provenInUse: origin.provenInUse,
                                    sd: origin.sd,
                                    su: origin.su,
                                    dd: origin.dd,
                                    du: origin.du,
                                } as SilDeviceType)}
                                >
                                    {lang.updateModel}
                                </Button>
                            </PageForm>
                        </RowStyled>
                    )
                }
                {
                    nestedDevicesNotEqual && disableUpdateModel && (
                        <RowStyled danger>
                            <PageForm>
                                <Feedback>{lang.subsystemModelCantBeUpdated}</Feedback>
                            </PageForm>
                        </RowStyled>
                    )
                }
            </Collapse>


            <PageForm>
                <Row>
                    <Col xs={12}>
                        <Button
                            variant="success"
                            row
                            onClick={() => {
                                saveMode = 1
                                updateAll()
                            }}
                        >
                            {updateAllState === PENDING ? <SpinnerIcon /> : lang.updateAll}
                        </Button>
                        <Button
                            variant="success"
                            row
                            onClick={() => {
                                saveMode = 2
                                onSave(target)
                            }}
                        >
                            {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                        </Button>
                        <Button row onClick={onClose}>{lang.cancel}</Button>
                    </Col>
                </Row>

                <ConfirmWrapper
                    show={recalculateCount > 0}
                    onClose={onCancelConfirm}
                >
                    <Confirm align="center">
                        <ConfirmTitle>{lang.recalculationTitle}</ConfirmTitle>
                        <ConfirmDescription align="start">
                            {lang.recalculationDescription.replace("{0}", recalculateCount.toString())}
                        </ConfirmDescription>
                        <ConfirmDescription align="start">{leftCalculations}</ConfirmDescription>

                        <ConfirmBottom>
                            <Button row onClick={onCancelConfirm}>{lang.cancel}</Button>
                            <Button
                                variant="success"
                                row
                                disabled={maxCalculations !== null && usedCalculations >= maxCalculations}
                                onClick={() => {
                                    save(target)
                                    setRecalculateCount(0)
                                }}
                            >
                                {lang.confirm}
                            </Button>
                        </ConfirmBottom>
                    </Confirm>
                </ConfirmWrapper>
            </PageForm>
        </div>
    )
}
export default Form
